/**
 * PDF Export for Quick Scan Price Results
 * Generates a styled PDF using jsPDF, then shares via native share sheet.
 */
import { jsPDF } from 'jspdf';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

const CONDITION_ORDER = ['nm', 'lp', 'mp', 'hp', 'dmg'];
const CONDITION_LABELS = { nm: 'NM', lp: 'LP', mp: 'MP', hp: 'HP', dmg: 'DMG' };

// Brand colors
const BLUE = [14, 165, 233];    // #0ea5e9
const GREEN = [34, 197, 94];    // #22c55e
const DARK = [15, 23, 42];      // #0f172a
const GRAY_900 = [17, 24, 39];
const GRAY_700 = [55, 65, 81];
const GRAY_500 = [107, 114, 128];
const GRAY_400 = [156, 163, 175];
const GRAY_200 = [229, 231, 235];
const GRAY_100 = [243, 244, 246];
const WHITE = [255, 255, 255];

function fmt(price) {
  if (price == null || price <= 0) return '--';
  return `$${price.toFixed(2)}`;
}

function roundedRect(doc, x, y, w, h, r, fillColor) {
  doc.setFillColor(...fillColor);
  doc.roundedRect(x, y, w, h, r, r, 'F');
}

function drawPageFooter(doc, pageW, pageH, pageNum, totalPages) {
  const footerY = pageH - 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...GRAY_400);
  doc.text('Generated by CardChase  |  Pricing via JustTCG', 14, footerY);
  doc.text(`Page ${pageNum} of ${totalPages}`, pageW - 14, footerY, { align: 'right' });
}

export async function generatePriceReportPdf(pricingCards) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const marginL = 14;
  const marginR = 14;
  const contentW = pageW - marginL - marginR;
  let y = 0;

  const date = new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
  const time = new Date().toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit',
  });

  const totalValue = pricingCards.reduce((sum, c) => sum + (c.price || 0), 0);
  const pricedCount = pricingCards.filter(c => c.price != null && c.price > 0).length;
  const avgValue = pricedCount > 0 ? totalValue / pricedCount : 0;
  const topCard = [...pricingCards].sort((a, b) => (b.price || 0) - (a.price || 0))[0];

  // =============================================
  // HEADER BANNER
  // =============================================
  doc.setFillColor(...DARK);
  doc.rect(0, 0, pageW, 28, 'F');

  // Accent bar
  doc.setFillColor(...BLUE);
  doc.rect(0, 28, pageW, 1.5, 'F');

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...WHITE);
  doc.text('Price Report', marginL, 14);

  // Subtitle
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...GRAY_400);
  doc.text('CardChase Quick Scan', marginL, 20);

  // Date right-aligned
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...GRAY_400);
  doc.text(`${date}  ${time}`, pageW - marginR, 14, { align: 'right' });

  y = 36;

  // =============================================
  // SUMMARY CARDS ROW
  // =============================================
  const cardH = 22;
  const gap = 6;
  const cardW = (contentW - gap * 2) / 3;

  // Card 1: Total Value
  roundedRect(doc, marginL, y, cardW, cardH, 3, GRAY_100);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...GRAY_500);
  doc.text('TOTAL VALUE', marginL + 5, y + 7);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...GREEN);
  doc.text(fmt(totalValue), marginL + 5, y + 17);

  // Card 2: Cards Priced
  const card2X = marginL + cardW + gap;
  roundedRect(doc, card2X, y, cardW, cardH, 3, GRAY_100);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...GRAY_500);
  doc.text('CARDS PRICED', card2X + 5, y + 7);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...GRAY_900);
  doc.text(`${pricedCount} / ${pricingCards.length}`, card2X + 5, y + 17);

  // Card 3: Average / Top Card
  const card3X = marginL + (cardW + gap) * 2;
  roundedRect(doc, card3X, y, cardW, cardH, 3, GRAY_100);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...GRAY_500);
  doc.text('AVERAGE PRICE', card3X + 5, y + 7);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...BLUE);
  doc.text(pricedCount > 0 ? fmt(avgValue) : '--', card3X + 5, y + 17);

  y += cardH + 10;

  // =============================================
  // TABLE HEADER
  // =============================================
  const colNum = marginL;
  const colName = marginL + 10;
  const colVariant = marginL + contentW - 72;
  const colCond = marginL + contentW - 42;
  const colPrice = marginL + contentW;

  roundedRect(doc, marginL, y, contentW, 8, 2, DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...WHITE);
  y += 5.5;
  doc.text('#', colNum + 2, y);
  doc.text('CARD', colName, y);
  doc.text('VARIANT', colVariant, y);
  doc.text('COND', colCond, y);
  doc.text('PRICE', colPrice, y, { align: 'right' });
  y += 5.5;

  // =============================================
  // CARD ROWS
  // =============================================
  const ROW_H = 14;

  // Pre-calculate total pages for footer
  let tempY = y;
  let pages = 1;
  for (let i = 0; i < pricingCards.length; i++) {
    if (tempY + ROW_H > pageH - 16) {
      pages++;
      tempY = 24; // after re-drawn header on new page
    }
    tempY += ROW_H;
  }
  // Account for footer section
  if (tempY + 30 > pageH - 16) pages++;
  const totalPages = pages;

  let currentPage = 1;

  for (let i = 0; i < pricingCards.length; i++) {
    const card = pricingCards[i];

    // New page check
    if (y + ROW_H > pageH - 16) {
      drawPageFooter(doc, pageW, pageH, currentPage, totalPages);
      doc.addPage();
      currentPage++;
      y = 12;

      // Mini header on continuation pages
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...GRAY_500);
      doc.text('Price Report (continued)', marginL, y);
      y += 6;

      // Re-draw table header
      roundedRect(doc, marginL, y, contentW, 8, 2, DARK);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...WHITE);
      y += 5.5;
      doc.text('#', colNum + 2, y);
      doc.text('CARD', colName, y);
      doc.text('VARIANT', colVariant, y);
      doc.text('COND', colCond, y);
      doc.text('PRICE', colPrice, y, { align: 'right' });
      y += 5.5;
    }

    // Alternating row background
    if (i % 2 === 0) {
      doc.setFillColor(...GRAY_100);
      doc.rect(marginL, y - 1, contentW, ROW_H, 'F');
    }

    // Row number
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...GRAY_400);
    doc.text(`${i + 1}`, colNum + 2, y + 4);

    // Card name (line 1)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...GRAY_900);
    const name = card.name || 'Unknown';
    const maxNameW = colVariant - colName - 4;
    let truncName = name;
    while (doc.getStringUnitWidth(truncName) * 9 / doc.internal.scaleFactor > maxNameW && truncName.length > 3) {
      truncName = truncName.slice(0, -4) + '...';
    }
    doc.text(truncName, colName, y + 4);

    // Set + number (line 2)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...GRAY_500);
    const setLine = `${card.setName || ''}${card.number ? ` #${card.number}` : ''}`;
    let truncSet = setLine;
    while (doc.getStringUnitWidth(truncSet) * 7 / doc.internal.scaleFactor > maxNameW && truncSet.length > 3) {
      truncSet = truncSet.slice(0, -4) + '...';
    }
    doc.text(truncSet, colName, y + 9);

    // Variant
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...GRAY_700);
    doc.text(card.variant || 'Normal', colVariant, y + 4);

    // Condition - with colored pill background
    const condText = card.condition || 'NM';
    const condTextW = doc.getStringUnitWidth(condText) * 7.5 / doc.internal.scaleFactor + 5;
    const condPillX = colCond - 1;
    const condPillY = y + 0.5;
    roundedRect(doc, condPillX, condPillY, condTextW, 5.5, 1.5, GRAY_200);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...GRAY_700);
    doc.text(condText, condPillX + 2.5, y + 4.5);

    // Price
    if (card.price != null && card.price > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...GREEN);
      doc.text(fmt(card.price), colPrice, y + 4.5, { align: 'right' });
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...GRAY_400);
      doc.text('N/A', colPrice, y + 4.5, { align: 'right' });
    }

    // Condition breakdown (small text, line 2 right side)
    if (card.pricingData?.conditions) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(...GRAY_400);
      const condParts = CONDITION_ORDER.map(key => {
        const p = card.pricingData.conditions[key]?.price;
        return `${CONDITION_LABELS[key]} ${fmt(p)}`;
      });
      doc.text(condParts.join('   '), colVariant, y + 9);
    }

    y += ROW_H;
  }

  // =============================================
  // FOOTER SUMMARY
  // =============================================
  if (y + 35 > pageH - 16) {
    drawPageFooter(doc, pageW, pageH, currentPage, totalPages);
    doc.addPage();
    currentPage++;
    y = 16;
  }

  y += 4;

  // Accent line
  doc.setFillColor(...BLUE);
  doc.rect(marginL, y, contentW, 1, 'F');
  y += 6;

  // Summary row
  const summaryH = 28;
  roundedRect(doc, marginL, y, contentW, summaryH, 3, DARK);

  // Left side stats
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...GRAY_400);
  doc.text(`${pricedCount} of ${pricingCards.length} cards priced`, marginL + 8, y + 9);

  if (topCard && topCard.price > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...GRAY_500);
    doc.text('Top card:', marginL + 8, y + 16);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...WHITE);
    const topName = (topCard.name || 'Unknown').substring(0, 35);
    doc.text(`${topName} (${fmt(topCard.price)})`, marginL + 24, y + 16);
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...GRAY_500);
  doc.text(`Avg: ${fmt(avgValue)}`, marginL + 8, y + 22);

  // Right side total
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...GRAY_400);
  doc.text('TOTAL VALUE', pageW - marginR - 8, y + 8, { align: 'right' });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...GREEN);
  doc.text(fmt(totalValue), pageW - marginR - 8, y + 22, { align: 'right' });

  y += summaryH + 6;

  // Attribution
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...GRAY_400);
  doc.text('Prices are estimates and may vary. Generated by CardChase.', pageW / 2, y, { align: 'center' });

  // Draw page footers on all pages
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    drawPageFooter(doc, pageW, pageH, p, pageCount);
  }

  // =============================================
  // EXPORT & SHARE
  // =============================================
  if (Capacitor.isNativePlatform()) {
    const base64 = doc.output('datauristring').split(',')[1];
    const fileName = `CardChase-PriceReport-${Date.now()}.pdf`;

    const writeResult = await Filesystem.writeFile({
      path: fileName,
      data: base64,
      directory: Directory.Cache,
    });

    await Share.share({
      title: 'Card Price Report',
      url: writeResult.uri,
      dialogTitle: 'Share Price Report',
    });
  } else {
    doc.save('CardChase-PriceReport.pdf');
  }
}
